import React from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import { useDispatch } from 'react-redux'
import { useFirebase } from 'react-redux-firebase'
import { actions as appActions } from '../App/reducer'
import {
  getFriendlyFirebaseErrorMessage,
  getMultiFactorLabel,
  isPhoneNumberValid,
  normalizePhoneNumber,
} from '../../utils/firebaseMultiFactor'

interface Props {
  onClose: () => void
}

export const PortalMultiFactorModal: React.FC<Props> = ({ onClose }) => {
  const dispatch = useDispatch()
  const firebase = useFirebase()
  const recaptchaContainerId = React.useMemo(
    () => `portal-mfa-recaptcha-${Math.random().toString(36).slice(2)}`,
    []
  )

  const recaptchaVerifierRef =
    React.useRef<Firebase.auth.RecaptchaVerifier | null>(null)
  const [enrolledFactors, setEnrolledFactors] = React.useState<
    Firebase.auth.MultiFactorInfo[]
  >([])
  const [phoneNumber, setPhoneNumber] = React.useState('')
  const [displayName, setDisplayName] = React.useState('')
  const [verificationId, setVerificationId] = React.useState<string | null>(
    null
  )
  const [verificationCode, setVerificationCode] = React.useState('')
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
  const [isSendingCode, setIsSendingCode] = React.useState(false)
  const [isSaving, setIsSaving] = React.useState(false)
  const [isRemovingUid, setIsRemovingUid] = React.useState<string | null>(null)

  const clearRecaptchaVerifier = React.useCallback(() => {
    try {
      recaptchaVerifierRef.current?.clear()
    } catch {
      return
    } finally {
      recaptchaVerifierRef.current = null
    }
  }, [])

  const currentUser = firebase.auth().currentUser

  const refreshEnrolledFactors = React.useCallback(async () => {
    const user = firebase.auth().currentUser

    if (user == null) {
      setEnrolledFactors([])
      return
    }

    await user.reload()
    setEnrolledFactors([...user.multiFactor.enrolledFactors])
  }, [firebase])

  React.useEffect(() => {
    void refreshEnrolledFactors()

    return () => {
      clearRecaptchaVerifier()
    }
  }, [clearRecaptchaVerifier, refreshEnrolledFactors])

  const resetEnrollmentForm = React.useCallback(() => {
    setPhoneNumber('')
    setDisplayName('')
    setVerificationId(null)
    setVerificationCode('')
    setErrorMessage(null)
    clearRecaptchaVerifier()
  }, [clearRecaptchaVerifier])

  const createRecaptchaVerifier = React.useCallback(async () => {
    if (recaptchaVerifierRef.current != null) {
      return recaptchaVerifierRef.current
    }

    const verifier = new Firebase.auth.RecaptchaVerifier(
      recaptchaContainerId,
      { size: 'normal' },
      Firebase.app()
    )

    await verifier.render()

    recaptchaVerifierRef.current = verifier
    return verifier
  }, [recaptchaContainerId])

  const onSendVerificationCode = React.useCallback(async () => {
    if (currentUser == null) {
      setErrorMessage('Sign in again before changing two-step verification.')
      return
    }

    if (!isPhoneNumberValid(phoneNumber)) {
      setErrorMessage(
        'Use a mobile number in international format, for example +15551234567.'
      )
      return
    }

    setIsSendingCode(true)
    setErrorMessage(null)

    try {
      const session = await currentUser.multiFactor.getSession()
      const phoneAuthProvider = new Firebase.auth.PhoneAuthProvider()
      const verifier = await createRecaptchaVerifier()
      const nextVerificationId = await phoneAuthProvider.verifyPhoneNumber(
        {
          phoneNumber: normalizePhoneNumber(phoneNumber),
          session,
        },
        verifier
      )

      setVerificationId(nextVerificationId)
    } catch (error) {
      setErrorMessage(
        getFriendlyFirebaseErrorMessage(
          error,
          'Unable to send a verification code right now.'
        )
      )
      clearRecaptchaVerifier()
    } finally {
      setIsSendingCode(false)
    }
  }, [
    clearRecaptchaVerifier,
    createRecaptchaVerifier,
    currentUser,
    firebase,
    phoneNumber,
  ])

  const onEnroll = React.useCallback(async () => {
    if (currentUser == null || verificationId == null) {
      setErrorMessage('Request a verification code before completing setup.')
      return
    }

    setIsSaving(true)
    setErrorMessage(null)

    try {
      const credential = Firebase.auth.PhoneAuthProvider.credential(
        verificationId,
        verificationCode.trim()
      )
      const assertion =
        Firebase.auth.PhoneMultiFactorGenerator.assertion(credential)

      await currentUser.multiFactor.enroll(
        assertion,
        displayName.trim() || undefined
      )

      dispatch(
        appActions.onAlert({
          severity: 'success',
          message: 'Two-step verification is now enabled.',
        })
      )
      resetEnrollmentForm()
      await refreshEnrolledFactors()
    } catch (error) {
      setErrorMessage(
        getFriendlyFirebaseErrorMessage(
          error,
          'Unable to complete two-step verification setup.'
        )
      )
    } finally {
      setIsSaving(false)
    }
  }, [
    currentUser,
    dispatch,
    displayName,
    refreshEnrolledFactors,
    resetEnrollmentForm,
    verificationCode,
    verificationId,
  ])

  const onRemoveFactor = React.useCallback(
    async (factor: Firebase.auth.MultiFactorInfo) => {
      if (currentUser == null) {
        setErrorMessage('Sign in again before changing two-step verification.')
        return
      }

      const confirmed = window.confirm(
        `Remove ${getMultiFactorLabel(factor)} from two-step verification?`
      )

      if (!confirmed) {
        return
      }

      setIsRemovingUid(factor.uid)
      setErrorMessage(null)

      try {
        await currentUser.multiFactor.unenroll(factor)
        dispatch(
          appActions.onAlert({
            severity: 'success',
            message: 'Two-step verification method removed.',
          })
        )
        await refreshEnrolledFactors()
      } catch (error) {
        setErrorMessage(
          getFriendlyFirebaseErrorMessage(
            error,
            'Unable to remove this two-step verification method.'
          )
        )
      } finally {
        setIsRemovingUid(null)
      }
    },
    [currentUser, dispatch, refreshEnrolledFactors]
  )

  return (
    <Dialog fullWidth maxWidth="sm" open onClose={onClose}>
      <DialogTitle>Two-Step Verification</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Add a phone number to protect your account with a second sign-in
            step.
          </Typography>
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          {enrolledFactors.length > 0 ? (
            <Alert severity="success">
              Two-step verification is currently enabled for this account.
            </Alert>
          ) : (
            <Alert severity="info">
              Two-step verification is not enabled yet.
            </Alert>
          )}
          {enrolledFactors.length > 0 ? (
            <List
              dense
              sx={{
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 1,
              }}
            >
              {enrolledFactors.map((factor) => (
                <ListItem
                  key={factor.uid}
                  secondaryAction={
                    <Button
                      color="error"
                      onClick={() => void onRemoveFactor(factor)}
                      disabled={isRemovingUid === factor.uid || isSaving}
                    >
                      Remove
                    </Button>
                  }
                >
                  <ListItemText
                    primary={getMultiFactorLabel(factor)}
                    secondary={`Enrolled ${new Date(
                      factor.enrollmentTime
                    ).toLocaleDateString()}`}
                  />
                </ListItem>
              ))}
            </List>
          ) : null}
          <TextField
            fullWidth
            label="Mobile number"
            placeholder="+15551234567"
            value={phoneNumber}
            onChange={(event) => setPhoneNumber(event.target.value)}
          />
          <TextField
            fullWidth
            label="Label (optional)"
            placeholder="Personal phone"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
          {verificationId ? (
            <TextField
              fullWidth
              autoFocus
              label="Verification code"
              value={verificationCode}
              onChange={(event) => setVerificationCode(event.target.value)}
            />
          ) : null}
          <Box id={recaptchaContainerId} sx={{ minHeight: 1 }} />
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: 'space-between',
          px: 3,
          pb: 2,
        }}
      >
        <Button onClick={onClose}>Close</Button>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            onClick={() => void onSendVerificationCode()}
            disabled={isSendingCode || isSaving}
          >
            {verificationId ? 'Resend code' : 'Send code'}
          </Button>
          <Button
            variant="contained"
            onClick={() => void onEnroll()}
            disabled={!verificationId || !verificationCode.trim() || isSaving}
          >
            Save
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}
